package com.siriusxm.pia.operations.dashboard

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

/**
 * Stylesheet for the dashboard layout system.
 *
 * The layout is intentionally implemented with flexbox + `flex-wrap` rather than CSS
 * grid. This matches the existing codebase convention (see `detail-grid.kt` in cmc-ui)
 * and, more importantly, delivers the per-widget responsive behaviour the dashboard needs:
 * each [Widget] declares its own `min-width`, and flexbox reflows widgets independently
 * as the viewport narrows — with no global breakpoint to configure.
 *
 * Only the grid geometry lives here; each widget's card chrome is the shared
 * [com.siriusxm.pia.components.Box] component, so there is no duplicated card styling.
 */
object DashboardStyles : StyleSheet() {

    /** Root container: a vertical stack of rows. Gap between rows is applied inline. */
    val dashboard by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        width(100.percent)
    }

    /**
     * A horizontal band. Children are laid out with `flex-wrap` so that widgets which can
     * no longer honour their `min-width` wrap onto the next line and expand to fill it.
     * `align-items: stretch` lets each widget's card fill the row height so short content
     * (e.g. a [StatCard]) matches taller siblings.
     */
    val row by style {
        display(DisplayStyle.Flex)
        flexWrap(FlexWrap.Wrap)
        width(100.percent)
        alignItems(AlignItems.Stretch)
    }

    /**
     * A widget cell. `flex-grow` is set inline to the widget's column span and `flex-basis`
     * is `0`, so cells in a row divide the available width proportionally to their spans
     * (a 12-column model), while `min-width` (also set inline) forces a cell to wrap to a
     * full-width line once the viewport is too narrow to honour it. The cell is itself a flex
     * column so the [com.siriusxm.pia.components.Box] inside (rendered with `fill = true`)
     * stretches to the full cell height.
     */
    val widgetCell by style {
        flexShrink(1)
        flexBasis("0%")
        maxWidth(100.percent)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
    }
}

/**
 * Root of a developer-authored dashboard.
 *
 * Owns the grid container and the vertical spacing between [Row]s. Dashboards are declared
 * declaratively in Kotlin with no manual CSS:
 *
 * ```
 * Dashboard {
 *     Row {
 *         Widget(span = 8, minWidth = 500, title = "CPU") { MetricsChart { ... } }
 *         Widget(span = 4, minWidth = 240) { StatCard("Active Producers", "12") }
 *     }
 *     Row {
 *         Widget(span = 12, minWidth = 320, title = "Logs") { LogsViewer { ... } }
 *     }
 * }
 * ```
 *
 * @param gap vertical spacing between rows. Defaults to `1rem` to match the app's section spacing.
 * @param content the dashboard body — a series of [Row]s.
 */
@Composable
fun Dashboard(
    gap: CSSNumeric = 1.cssRem,
    content: @Composable () -> Unit
) {
    Div({
        classes(DashboardStyles.dashboard)
        style { gap(gap) }
    }) {
        content()
    }
}
